/* ──────────────────────────────────────────────────────────────────
   HEA Cart Drawer — vanilla JS controller.

   Replaces Rebuy SmartCart. Owns:
     • Drawer open/close, scroll lock, focus trap, ESC-to-close
     • Universal ATC interception (every form[action*="/cart/add"])
     • /cart.js state mirror, /cart/add.js, /cart/change.js wrappers
     • Section-render re-fetch of drawer body after every mutation
     • Custom events: cart:change, cart:open, cart:close

   Why a delegated submit listener instead of binding each form:
   forms get inserted dynamically (quick-shop modals, future widgets) —
   binding once on init misses them. Delegation on document catches
   every form regardless of when it entered the DOM. This is the fix
   for the Rebuy "ATC sometimes doesn't fire" bug.

   Exposes: window.HEACart with open(), close(), addItem(), change(),
   refresh(), getState().
   ────────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  if (window.HEACart) return;

  var SECTION_ID = 'api-cart-drawer';

  var HEACart = {
    state: { item_count: 0, total_price: 0, items: [] },
    drawer: null,
    body: null,
    isOpen: false,
    inFlight: false,
    lastFocus: null,
    pendingAdd: null,

    init: function () {
      this.drawer = document.getElementById('cart-drawer');
      if (!this.drawer) {
        // Drawer not in DOM (e.g., /cart page where we don't render it).
        // Still bind ATC so adding from PDP redirects to /cart naturally.
        this.bindATC();
        return;
      }
      this.body = this.drawer.querySelector('[data-cart-body]');

      this.bindCloseHandlers();
      this.bindKeyHandlers();
      this.bindBodyDelegation();
      this.bindATC();
      this.bindOpenTriggers();

      // Hydrate state from /cart.js so client mirror is accurate even if
      // the page was served from cache or item count changed in another tab.
      this.hydrate();

      document.body.setAttribute('data-cart-bound', 'true');
    },

    /* ───── State ──────────────────────────────────────────────── */

    hydrate: function () {
      var self = this;
      return fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) { self.setState(cart); return cart; })
        .catch(function () { return null; });
    },

    setState: function (cart) {
      if (!cart) return;
      this.state = cart;
      this.updateCount();
      this.updateSubtotal();
      this.toggleFooter();
      document.dispatchEvent(new CustomEvent('cart:change', { detail: { cart: cart } }));
    },

    getState: function () { return this.state; },

    updateCount: function () {
      var count = (this.state && this.state.item_count) || 0;
      var els = document.querySelectorAll('[data-cart-count]');
      for (var i = 0; i < els.length; i++) els[i].textContent = count;
      // Header bubble dot — keep existing class hook so header CSS still works.
      var dot = document.querySelector('.Header__CartDot');
      if (dot) dot.classList.toggle('is-visible', count > 0);
    },

    updateSubtotal: function () {
      // The footer's SECURE CHECKOUT amount lives outside the re-rendered
      // body, so we update its text directly.
      //
      // B2B WPD-aware: prefer the sum of `[data-line-price-cents]` attributes
      // on the rendered line items — those reflect Wholesale Pricing Discount
      // (server-side Liquid pipeline). Fall back to /cart.js total_price for
      // logged-out / non-dealer customers where wcp_line_price === final_price.
      var els = document.querySelectorAll('[data-cart-subtotal]');
      var lines = document.querySelectorAll('[data-line-price-cents]');
      var total = 0;
      var haveDomTotal = false;
      if (lines.length) {
        for (var j = 0; j < lines.length; j++) {
          var c = parseInt(lines[j].getAttribute('data-line-price-cents'), 10);
          if (!isNaN(c)) { total += c; haveDomTotal = true; }
        }
      }
      if (!haveDomTotal) total = (this.state && this.state.total_price) || 0;
      var formatted = this.formatMoney(total);
      for (var i = 0; i < els.length; i++) els[i].textContent = formatted;
    },

    toggleFooter: function () {
      // Hide footer when cart is empty (no checkout button needed).
      if (!this.drawer) return;
      var footer = this.drawer.querySelector('.cart-drawer__footer');
      if (!footer) return;
      footer.hidden = !(this.state && this.state.item_count > 0);
    },

    formatMoney: function (cents) {
      // Shopify exposes Shopify.formatMoney via option_selection.js on most
      // themes. Fall back to a simple USD formatter if it's not available.
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        var fmt = (window.theme && window.theme.moneyFormat) || '${{amount}}';
        return window.Shopify.formatMoney(cents, fmt);
      }
      return '$' + (cents / 100).toFixed(2);
    },

    /* ───── Open / close ───────────────────────────────────────── */

    open: function () {
      if (!this.drawer || this.isOpen) return;
      this.isOpen = true;
      this.lastFocus = document.activeElement;
      this.drawer.classList.add('is-visible');
      this.drawer.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('cart-drawer-open');
      document.body.classList.add('cart-drawer-open');
      // Focus the close button so screen readers announce + Esc works immediately.
      var firstFocus = this.drawer.querySelector('[data-cart-close]');
      if (firstFocus) firstFocus.focus({ preventScroll: true });
      document.dispatchEvent(new CustomEvent('cart:open', { detail: { cart: this.state } }));
    },

    close: function () {
      if (!this.drawer || !this.isOpen) return;
      this.isOpen = false;
      this.drawer.classList.remove('is-visible');
      this.drawer.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('cart-drawer-open');
      document.body.classList.remove('cart-drawer-open');
      if (this.lastFocus && typeof this.lastFocus.focus === 'function') {
        try { this.lastFocus.focus({ preventScroll: true }); } catch (e) {}
      }
      document.dispatchEvent(new CustomEvent('cart:close'));
    },

    bindOpenTriggers: function () {
      // Intercept anything that wants to open the cart:
      //   • Header cart icons (B2B): [data-action="open-drawer"][data-drawer-id="cart-drawer"]
      //   • Legacy/B2C: [data-action="open-drawer"][data-drawer-id="sidebar-cart"]
      //   • Anything explicitly tagged: [data-cart-open]
      // Plain <a href="/cart"> links are NOT intercepted — those should
      // navigate to the cart page (footer links, etc.) unless they also
      // carry one of the markers above.
      var self = this;
      document.addEventListener('click', function (e) {
        var trigger = e.target.closest(
          '[data-cart-open],' +
          '[data-action="open-drawer"][data-drawer-id="cart-drawer"],' +
          '[data-action="open-drawer"][data-drawer-id="sidebar-cart"]'
        );
        if (!trigger) return;
        // Don't hijack modifier-clicks (open-in-new-tab etc.)
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        self.open();
      }, false);
    },

    bindCloseHandlers: function () {
      var self = this;
      this.drawer.addEventListener('click', function (e) {
        if (e.target.closest('[data-cart-close]')) {
          e.preventDefault();
          self.close();
        }
      });
    },

    bindKeyHandlers: function () {
      var self = this;
      document.addEventListener('keydown', function (e) {
        if (!self.isOpen) return;
        if (e.key === 'Escape') {
          e.preventDefault();
          self.close();
        } else if (e.key === 'Tab') {
          self.trapFocus(e);
        }
      });
    },

    trapFocus: function (e) {
      var focusables = this.drawer.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    },

    /* ───── Body delegation (qty + remove) ─────────────────────── */

    bindBodyDelegation: function () {
      var self = this;
      this.drawer.addEventListener('click', function (e) {
        var dec  = e.target.closest('[data-cart-decrease]');
        var inc  = e.target.closest('[data-cart-increase]');
        var rem  = e.target.closest('[data-cart-remove]');

        if (dec) {
          e.preventDefault();
          var line = parseInt(dec.dataset.line, 10);
          var qty  = parseInt(dec.dataset.quantity, 10);
          if (!isNaN(line) && !isNaN(qty)) self.change(line, Math.max(0, qty));
        } else if (inc) {
          e.preventDefault();
          var line2 = parseInt(inc.dataset.line, 10);
          var qty2  = parseInt(inc.dataset.quantity, 10);
          if (!isNaN(line2) && !isNaN(qty2)) self.change(line2, qty2);
        } else if (rem) {
          e.preventDefault();
          var line3 = parseInt(rem.dataset.line, 10);
          if (!isNaN(line3)) self.change(line3, 0);
        }
      });
    },

    /* ───── ATC interception (theme-wide) ──────────────────────── */

    bindATC: function () {
      var self = this;

      // Path 1 — Prestige's own ATC: it binds click on
      // [data-action="add-to-cart"], does its own /cart/add.js fetch,
      // dispatches a `product:added` CustomEvent on success. Hook that
      // event to refresh + open the drawer (no double-add).
      document.addEventListener('product:added', function () {
        self.refresh().then(function () { self.open(); });
      }, false);

      // Path 2 — generic form submit fallback for any ATC form Prestige
      // didn't bind (quick-shop modals, future widgets, recommended-inserts
      // form). Submit-level delegation catches dynamically-injected forms.
      document.addEventListener('submit', function (e) {
        var form = e.target;
        if (!form || form.tagName !== 'FORM') return;
        var action = form.getAttribute('action') || '';
        if (action.indexOf('/cart/add') === -1) return;

        // Honor explicit return_to: user is intentionally going to /cart.
        var returnTo = form.querySelector('[name="return_to"]');
        if (returnTo && returnTo.value) return;

        // If Prestige's own click-handler already fired and prevented the
        // native submit, this branch never runs — that's fine, Path 1
        // (product:added) handles the drawer open. Submit-only forms
        // (no [data-action="add-to-cart"] button) flow through here.
        e.preventDefault();
        self.handleATCForm(form);
      }, false);
    },

    handleATCForm: function (form) {
      var self = this;
      var formData = new FormData(form);
      var submitBtn = form.querySelector('[type="submit"], [name="add"]');
      var origLabel = submitBtn ? submitBtn.innerHTML : null;
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.classList.add('is-loading');
      }

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData
      })
        .then(function (r) {
          if (!r.ok) {
            return r.json().then(function (j) {
              throw new Error((j && (j.description || j.message)) || 'Could not add to cart.');
            });
          }
          return r.json();
        })
        .then(function () { return self.refresh(); })
        .then(function () { self.open(); })
        .catch(function (err) {
          console.error('[cart] ATC failed:', err);
          self.showError(err && err.message ? err.message : 'Could not add to cart.');
        })
        .then(function () {
          if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.classList.remove('is-loading');
            if (origLabel != null) submitBtn.innerHTML = origLabel;
          }
        });
    },

    /* ───── Cart mutations ─────────────────────────────────────── */

    addItem: function (variantId, quantity, properties) {
      var self = this;
      quantity = quantity || 1;
      var payload = { items: [{ id: variantId, quantity: quantity }] };
      if (properties) payload.items[0].properties = properties;

      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      })
        .then(function (r) {
          if (!r.ok) {
            return r.json().then(function (j) {
              throw new Error((j && (j.description || j.message)) || 'Could not add to cart.');
            });
          }
          return r.json();
        })
        .then(function () { return self.refresh(); })
        .then(function () { self.open(); })
        .catch(function (err) {
          console.error('[cart] addItem failed:', err);
          self.showError(err && err.message ? err.message : 'Could not add to cart.');
          throw err;
        });
    },

    change: function (line, quantity) {
      var self = this;
      if (this.inFlight) return Promise.resolve();
      this.inFlight = true;

      // Optimistic visual: dim the affected row while in flight.
      var row = this.body && this.body.querySelector('[data-line="' + line + '"]');
      if (row) {
        var rowEl = row.closest('[data-cart-line]');
        if (rowEl) rowEl.classList.add('is-loading');
      }

      return fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: line, quantity: quantity })
      })
        .then(function (r) { return r.json(); })
        .then(function () { return self.refresh(); })
        .catch(function (err) {
          console.error('[cart] change failed:', err);
          self.showError('Could not update cart.');
        })
        .then(function () { self.inFlight = false; });
    },

    refresh: function () {
      var self = this;
      return Promise.all([
        fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); }),
        fetch('/?section_id=' + SECTION_ID, { headers: { Accept: 'text/html' } }).then(function (r) { return r.text(); })
      ]).then(function (results) {
        var cart = results[0];
        var html = results[1];
        self.setState(cart);
        if (self.body && html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          // The section endpoint renders body innerHTML directly; pull it.
          var section = doc.querySelector('.shopify-section') || doc.body;
          if (section) self.body.innerHTML = section.innerHTML;
        }
      });
    },

    /* ───── Error surfacing ────────────────────────────────────── */

    showError: function (msg) {
      // Lightweight inline toast inside the drawer. Falls back to alert if
      // the drawer isn't present (e.g., adding from /cart page).
      if (!this.drawer) {
        try { window.alert(msg); } catch (e) {}
        return;
      }
      var toast = this.drawer.querySelector('[data-cart-toast]');
      if (!toast) {
        toast = document.createElement('div');
        toast.className = 'cart-drawer__toast';
        toast.setAttribute('data-cart-toast', '');
        toast.setAttribute('role', 'alert');
        this.drawer.querySelector('.cart-drawer__panel').appendChild(toast);
      }
      toast.textContent = msg;
      toast.classList.add('is-visible');
      clearTimeout(this._toastTimer);
      this._toastTimer = setTimeout(function () {
        toast.classList.remove('is-visible');
      }, 3500);
    }
  };

  window.HEACart = HEACart;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { HEACart.init(); });
  } else {
    HEACart.init();
  }
})();
